/** @jsxImportSource @emotion/react */
import { faAngleRight } from "@fortawesome/pro-regular-svg-icons";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { useIsMutating } from "@tanstack/react-query";
import { triggerCheckBusinessRules } from "../../../actions/Checks/actions";
import { Layer, Related } from "../../../actions/Layers/constants";
import useConfig from "../../../actions/Tenants/config/configHook";
import { DeviceResultActions } from "../../../actions/Tenants/config/constants";
import { FormField } from "../../../actions/Tenants/config/constantsTyped";
import { useCreateCheckMutation } from "../../../hooks/mutations/useCheckMutations";
import {
    useAddLayerParentMutation,
    useRemoveLayerParentMutation,
} from "../../../hooks/mutations/useLayerMutations";
import usePatchLayer from "../../../hooks/usePatchLayer";
import { getFilledArrayOrDefault, isFilledArray } from "../../../utils";
import { findChanges } from "../../../utils/findChanges";
import useNavigateFrom from "../../../utils/useNavigateFrom";
import MetaForm, { metaFormIsValid } from "../../Forms/MetaForm";
import { Button } from "../../Helper/Button";
import { ShowLinkToLocationAlert } from "./Layer/StartCheckForm";
import { useSwitchTabContext } from "./fields/useSwitchTab";

const getSamplePalletQuestion = (layers: Layer[]) => ({
    label: "What is the sample pallet?",
    type: layers.length > 6 ? "single-select" : "radio",
    name: "sample_pallet_layer_id",
    options: layers.map((l) => ({
        label: l.label,
        value: l.id,
    })),
    required: true,
});

export interface StartCheckFromOverviewModalProps {
    isOpen: boolean;
    onClose: () => void;
    layer: Layer;
    checkLocationValue: string;
    groupItems?: Layer[];
    /** When true, groupItems is the checkbox selection; choice is "Pallet" | "Selection (N)" */
    selectionMode?: boolean;
    /** Called after check is created successfully (e.g. to clear selection) */
    onSuccess?: () => void;
    /** Called to refresh the list after a no-flow check. Overrides refreshTab from useSwitchTabContext. */
    onRefresh?: () => void;
    /** Extra fields merged into the check creation payload (e.g. location_id for ripening room). */
    extraCheckPayload?: Record<string, any>;
}

export default function StartCheckFromOverviewModal({
    isOpen,
    onClose,
    layer,
    checkLocationValue,
    groupItems = [],
    selectionMode = false,
    onSuccess,
    onRefresh,
    extraCheckPayload,
}: StartCheckFromOverviewModalProps) {
    const config = useConfig();
    const dispatch = useDispatch();
    const patchLayer = usePatchLayer();
    const addLayerParentMutation = useAddLayerParentMutation();
    const removeLayerParentMutation = useRemoveLayerParentMutation();
    const createCheckMutation = useCreateCheckMutation();
    const params = useParams();
    const tenantId = params.tenant_id as string;
    const { navigateTo } = useNavigateFrom();
    const { refreshTab } = useSwitchTabContext();
    const refresh = onRefresh ?? refreshTab;

    const layerIsUpdating = useIsMutating() > 0;
    const checkIsUpdating = useIsMutating() > 0;

    const [layerFormState, setLayerFormState] = useState<Record<string, any>>({});
    const [createCheckData, setCreateCheckData] = useState<Record<string, any>>({});

    const groupItemIdsKey = useMemo(() => groupItems.map((g) => g.id).join(","), [groupItems]);

    const showSamplePalletChoice = groupItems.length > 1;
    const sampleLayer = showSamplePalletChoice
        ? (
            groupItems.find(
                (l) => l.id === (createCheckData?.sample_pallet_layer_id ?? groupItems[0]?.id)
            ) || groupItems[0] || layer
        )
        : layer;

    const location_config = config.get_check_type_config(sampleLayer, { location: checkLocationValue });

    useEffect(() => {
        if (isOpen) {
            setLayerFormState({});
            const shouldPickSamplePallet = groupItems.length > 1;
            setCreateCheckData(
                shouldPickSamplePallet
                    ? { sample_pallet_layer_id: groupItems[0]?.id }
                    : {}
            );
        }
    }, [isOpen, layer?.id, checkLocationValue, groupItemIdsKey]);

    useEffect(() => {
        if (!isOpen || !showSamplePalletChoice) return;
        setLayerFormState({});
    }, [createCheckData?.sample_pallet_layer_id, isOpen, showSamplePalletChoice]);

    const samplePalletQuestion = getSamplePalletQuestion(groupItems);
    const checkMetaForm = location_config
        ? getFilledArrayOrDefault(location_config.pre_check_form).map((field: any) => (
            sampleLayer[field.default_value_from_layer_key]
                ? { ...field, default_value: sampleLayer[field.default_value_from_layer_key] }
                : field
        ))
        : [];

    const isDisabled = () => {
        if (!location_config) return true;
        if (
            location_config.device_result_action === DeviceResultActions.pressure_advice
            && !config.is_location_calibrated(location_config, sampleLayer)
        ) {
            return true;
        }
        if (
            location_config.device_result_action === DeviceResultActions.setup_pressure_calibration
            && config.is_location_calibrated(location_config, sampleLayer)
        ) {
            return true;
        }
        if (layerIsUpdating || checkIsUpdating) return true;
        if (String((sampleLayer as any)[`start_${location_config.value}_disabled`]) === "1") {
            return true;
        }
        return false;
    };

    const submit = () => {
        if (!location_config || isDisabled()) return;
        const layerPayload = { ...sampleLayer, ...layerFormState };
        if (
            location_config.meta_form_layer
            && !metaFormIsValid(location_config.meta_form_layer as FormField[], layerPayload)
        ) {
            return;
        }
        if (isFilledArray(checkMetaForm) && !metaFormIsValid(checkMetaForm as any, createCheckData)) {
            return;
        }
        if (showSamplePalletChoice && !metaFormIsValid([samplePalletQuestion] as any, createCheckData)) {
            return;
        }

        const runCheckOnMultiple = groupItems.length > 1;
        const sampleLayerId = showSamplePalletChoice
            ? (createCheckData?.sample_pallet_layer_id ?? groupItems[0]?.id)
            : layer.id;

        const layerIds = runCheckOnMultiple
            ? [
                sampleLayerId,
                ...groupItems.map((l) => l.id).filter((id) => id !== sampleLayerId),
            ]
            : [layer.id];

        const primaryLayerId = sampleLayerId || layer.id;

        const applyLayerPatches = () => {
            const promises: Promise<any>[] = [];
            if (location_config.meta_form_layer) {
                (location_config.meta_form_layer as FormField[]).forEach((field) => {
                    const name = (field as any).name || (field as any).field;
                    if (name === "parents") {
                        const currentParents = (sampleLayer.parents || []) as Related[];
                        const newParents = (layerFormState.parents || layerPayload.parents || []) as Related[];
                        const { toBeAdded, toBeRemoved } = findChanges(
                            currentParents,
                            newParents,
                            (x, y) => x.id === y.id
                        );
                        toBeAdded.forEach((parent) => {
                            promises.push(addLayerParentMutation.mutateAsync({
                                layer: sampleLayer,
                                parentLayerId: parent.id,
                            }));
                        });
                        toBeRemoved.forEach((parent) => {
                            promises.push(removeLayerParentMutation.mutateAsync({
                                layer: sampleLayer,
                                parentLayerId: parent.id,
                            }));
                        });
                    } else if (layerFormState[name] !== undefined) {
                        promises.push(
                            patchLayer(sampleLayer, name, layerFormState[name], true)
                        );
                    }
                });
            }
            return Promise.all(promises);
        };

        let location = location_config.value;
        if (location_config.require_another_check) {
            const checks = (sampleLayer as any).checks || [];
            const hasRequired = checks.find((i: any) => i.location === location_config.require_another_check);
            const hasPrevious = checks.filter((i: any) => i.location === location_config.value).length > 0;
            if (!hasRequired && !hasPrevious) {
                location = location_config.require_another_check;
            }
        }

        const checkPayload: any = {
            layer_id: layerIds,
            location,
            is_manual: location_config.is_manual || false,
            ...(extraCheckPayload || {}),
        };
        const { pressure_calibration_settings } = location_config;
        const requested_calibration_stage = pressure_calibration_settings?.calibration_stage || false;
        if (requested_calibration_stage) {
            checkPayload.requested_calibration_stage = requested_calibration_stage;
        }
        if (createCheckData?.is_manual !== undefined) {
            checkPayload.is_manual = Boolean(Number(createCheckData.is_manual));
        }
        if (location_config.is_linked_to_latest_location) {
            checkPayload.location_id = sampleLayer.latest_location?.location_id;
        }
        if (runCheckOnMultiple && layer.group_key && !selectionMode) {
            checkPayload.group_key = layer.group_key;
            checkPayload.group_label = layer.group_label;
        }

        const postCheck = { ...checkPayload, ...(createCheckData || {}) };
        delete postCheck.sample_pallet_layer_id;

        applyLayerPatches().then(() => {
            createCheckMutation.mutateAsync({ tenantId, data: postCheck }).then((check) => {
                if (check) {
                    onSuccess?.();
                    onClose();
                    if (location_config?.flow?.length === 0) {
                        dispatch(
                            triggerCheckBusinessRules(tenantId, check.check_id) as any
                        ).then(() => {
                            refresh();
                        });
                        return;
                    }
                    navigateTo(
                        `/layer/${primaryLayerId}/add-check/${check.check_id}/${location_config?.flow?.[0]?.path}`
                    );
                }
            });
        });
    };

    if (!location_config) {
        return null;
    }

    return (
        <Modal isOpen={isOpen} size="md" toggle={onClose}>
            <ModalHeader toggle={onClose}>{location_config.text}</ModalHeader>
            <ModalBody>
                <ShowLinkToLocationAlert location_config={location_config} layer={sampleLayer} />
                <div>
                    {showSamplePalletChoice && (
                        <MetaForm
                            meta={[samplePalletQuestion]}
                            object={createCheckData}
                            config={config}
                            setValue={(field, value) => setCreateCheckData((prev) => ({ ...prev, [field]: value }))}
                        />
                    )}
                    {isFilledArray(location_config.meta_form_layer) && (
                        <MetaForm
                            key={`layer-${sampleLayer.id}`}
                            meta={location_config.meta_form_layer as any}
                            object={{ ...sampleLayer, ...layerFormState }}
                            config={config}
                            setValue={(field, value) => setLayerFormState((prev) => ({ ...prev, [field]: value }))}
                        />
                    )}
                    {isFilledArray(checkMetaForm) && (
                        <MetaForm
                            key="check"
                            meta={checkMetaForm}
                            object={createCheckData}
                            config={config}
                            setValue={(field, value) => setCreateCheckData((prev) => ({ ...prev, [field]: value }))}
                            extra_context={{
                                fruit_variety: sampleLayer.fruit_variety || undefined,
                                fruit_type: sampleLayer.fruit_type || undefined,
                            }}
                        />
                    )}
                </div>
            </ModalBody>
            <ModalFooter>
                <Button color="light" onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    color="success"
                    disabled={isDisabled()}
                    onClick={submit}
                    icon={faAngleRight}
                >
                    {location_config?.flow?.length ? "Start" : "Submit"}
                </Button>
            </ModalFooter>
        </Modal>
    );
}
